import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

import { useClientInfo } from "../utils/commonImports";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawClientHeader } from "../utils/reportClientHeader";
import { addPdfFooter } from "../utils/reportFooter";

/* ================================
   SINGLE SOURCE OF TRUTH
================================ */
const VEHICLE_COLUMNS = [
  { key: "vehicle_number", label: "Vehicle No" },
  { key: "vehicle_group", label: "Group" },
  { key: "vehicle_type_name", label: "Type" },
  { key: "vehicle_fuel_type", label: "Fuel" },
  { key: "vehicle_ownership", label: "Ownership" },
  { key: "vehicle_location", label: "Location" },
  { key: "vehicle_driver_name", label: "Driver" },
  { key: "vehicle_driver_mobile_no", label: "Driver Mobile" },
  { key: "vehicle_color", label: "Color" },
  { key: "vehicle_model", label: "Model" },
  { key: "fixed_mileage", label: "Fixed Mileage" },
  { key: "active", label: "Status" },
];

export default function VehicleReport() {

  const { clientId, client } = useClientInfo();

  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState("");
  const [fuel, setFuel] = useState("");
  const [ownership, setOwnership] = useState("");
  const [status, setStatus] = useState("");

  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  /* ========================
        LOAD REPORT
  ======================== */
  const loadReport = async () => {
    const res = await axios.get("/api/vehicles/report", {
      params: {
        client_id: clientId,
        search,
        fuel,
        ownership,
        status,
      },
    });

    setRows(res.data || []);
  };

  useEffect(() => {
    if (clientId) loadReport();
  }, [clientId]);

  /* ========================
        SORT
  ======================== */
  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setOrder(newOrder);
    setOrderBy(field);

    const sorted = [...rows].sort((a, b) => {
      if ((a[field] || "") < (b[field] || ""))
        return newOrder === "asc" ? -1 : 1;
      if ((a[field] || "") > (b[field] || ""))
        return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setRows(sorted);
  };

  /* ========================
        PDF GENERATOR
  ======================== */
  const generateVehiclePDF = () => {

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const headerEndY = drawClientHeader(doc, client);

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text("Vehicle Report", 14, headerEndY);

    const columns = VEHICLE_COLUMNS.map(c => c.label);
    const body = rows.map(r =>
      VEHICLE_COLUMNS.map(c => r[c.key])
    );

    autoTable(doc, {
      head: [columns],
      body,
      startY: headerEndY + 6,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
    });
 // =========================
  // FOOTER (ALWAYS LAST ❗)
  // =========================
  addPdfFooter(doc);
    return doc;
  };

  /* ========================
      PRINT / PDF / EXCEL
  ======================== */
  const handlePrint = () => {
    const doc = generateVehiclePDF();
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);

    iframe.onload = () => iframe.contentWindow.print();
  };

  const handlePDF = () => {
    generateVehiclePDF().save("Vehicle_Report.pdf");
  };

  const handleExcel = () => {

    const data = rows.map(r => {
      const obj = {};
      VEHICLE_COLUMNS.forEach(c => {
        obj[c.label] = r[c.key];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
    XLSX.writeFile(wb, "Vehicle_Report.xlsx");
  };

  /* ========================
        UI
  ======================== */
  return (
    <Paper className="print-report" sx={{ p: 3 }}>

      <Typography variant="h6" fontWeight={700} mb={1.5}>
        Vehicle Report
      </Typography>

      {/* FILTERS */}
      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">

        <TextField
          size="small"
          label="Search Vehicle / Driver / Owner"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />

        <TextField
          size="small"
          select
          label="Fuel"
          value={fuel}
          onChange={(e) => setFuel(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Petrol">Petrol</MenuItem>
          <MenuItem value="Diesel">Diesel</MenuItem>
          <MenuItem value="CNG">CNG</MenuItem>
          <MenuItem value="EV">EV</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Ownership"
          value={ownership}
          onChange={(e) => setOwnership(e.target.value)}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Own">Own</MenuItem>
          <MenuItem value="Attach">Attach</MenuItem>
          <MenuItem value="Vendor">Vendor</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Yes">Active</MenuItem>
          <MenuItem value="No">Inactive</MenuItem>
        </TextField>

        <Button variant="contained" size="small" onClick={loadReport}>
          Search
        </Button>

        <Button variant="outlined" size="small" onClick={handlePrint}>
          Print
        </Button>

        <Button variant="outlined" size="small" color="error" onClick={handlePDF}>
          PDF
        </Button>

        <Button variant="outlined" size="small" color="success" onClick={handleExcel}>
          Excel
        </Button>

      </Box>

      {/* TABLE */}
      <Table size="small">

        <TableHead sx={{ background: "#f1f5f9" }}>
          <TableRow>
            {VEHICLE_COLUMNS.map(col => (
              <TableCell
                key={col.key}
                sx={{ fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>

          {rows.map(r => (
            <TableRow key={r.vehicle_id}>
              {VEHICLE_COLUMNS.map(col => (
                <TableCell key={col.key} sx={{ fontSize: 12 }}>
                  {r[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={VEHICLE_COLUMNS.length} align="center">
                No records found
              </TableCell>
            </TableRow>
          )}

        </TableBody>

      </Table>

    </Paper>
  );
}
